import { useState, useEffect } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { formatApiError } from "@/lib/api";
import { Loader2, ArrowRight } from "lucide-react";
import api from "@/lib/api";

export default function ResetPasswordPage() {
  const [password, setPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [error, setError] = useState("");
  const [success, setSuccess] = useState("");
  const [loading, setLoading] = useState(false);
  const [token, setToken] = useState("");
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();

  useEffect(() => {
    const tokenParam = searchParams.get("token");
    if (tokenParam) {
      setToken(tokenParam);
    } else {
      setError("Invalid reset link. No token provided.");
    }
  }, [searchParams]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError("");
    setSuccess("");

    if (password !== confirmPassword) {
      setError("Passwords do not match");
      return;
    }

    if (password.length < 6) {
      setError("Password must be at least 6 characters");
      return;
    }

    setLoading(true);
    try {
      await api.post("/auth/reset-password", { token, new_password: password });
      setSuccess("Password reset successfully! Redirecting to login...");
      setTimeout(() => navigate("/login"), 2000);
    } catch (err) {
      setError(formatApiError(err.response?.data?.detail) || err.message);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen flex" data-testid="reset-password-page">
      {/* Left - Form */}
      <div className="flex-1 flex items-center justify-center p-8 md:p-12">
        <div className="w-full max-w-sm">
          <div className="mb-12">
            <h1
              className="text-4xl sm:text-5xl tracking-tighter font-medium text-slate-950"
              style={{ fontFamily: "Outfit, sans-serif" }}
            >
              PayrollKE
            </h1>
            <p className="mt-2 text-sm text-slate-500">
              Set your new password
            </p>
          </div>

          <form onSubmit={handleSubmit} className="space-y-5">
            <div>
              <label className="text-xs font-semibold uppercase tracking-[0.2em] text-slate-500 block mb-2">
                New Password
              </label>
              <input
                data-testid="reset-password-input"
                type="password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                className="bg-white border border-slate-200 rounded-sm px-4 py-2.5 w-full focus:outline-none focus:ring-2 focus:ring-[#002FA7]/30 focus:border-[#002FA7] transition-all text-sm"
                placeholder="Enter new password"
                required
              />
            </div>

            <div>
              <label className="text-xs font-semibold uppercase tracking-[0.2em] text-slate-500 block mb-2">
                Confirm Password
              </label>
              <input
                data-testid="reset-confirm-password-input"
                type="password"
                value={confirmPassword}
                onChange={(e) => setConfirmPassword(e.target.value)}
                className="bg-white border border-slate-200 rounded-sm px-4 py-2.5 w-full focus:outline-none focus:ring-2 focus:ring-[#002FA7]/30 focus:border-[#002FA7] transition-all text-sm"
                placeholder="Confirm new password"
                required
              />
            </div>

            {error && (
              <div
                data-testid="reset-error"
                className="bg-[#FEF2F2] border border-[#EF4444] text-[#991B1B] text-sm px-4 py-3 rounded-sm"
              >
                {error}
              </div>
            )}

            {success && (
              <div
                data-testid="reset-success"
                className="bg-[#F0FDF4] border border-[#22C55E] text-[#15803D] text-sm px-4 py-3 rounded-sm"
              >
                {success}
              </div>
            )}

            <button
              data-testid="reset-submit-button"
              type="submit"
              disabled={loading || !token}
              className="bg-[#002FA7] hover:bg-[#002480] text-white rounded-sm px-6 py-2.5 font-medium transition-colors focus:ring-2 focus:ring-[#002FA7]/30 w-full flex items-center justify-center gap-2 text-sm disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {loading ? (
                <Loader2 className="h-4 w-4 animate-spin" />
              ) : (
                <>
                  Reset Password <ArrowRight className="h-4 w-4" />
                </>
              )}
            </button>
          </form>
        </div>
      </div>

      {/* Right - Image */}
      <div className="hidden lg:block lg:w-1/2 relative">
        <img
          src="https://images.unsplash.com/photo-1513694203232-719a280e022f?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjA1NjZ8MHwxfHNlYXJjaHwxfHxtaW5pbWFsaXN0JTIwYWJzdHJhY3QlMjBhcmNoaXRlY3R1cmUlMjB3aGl0ZSUyMGNsZWFufGVufDB8fHx8MTc3NjI1MjM3MHww&ixlib=rb-4.1.0&q=85"
          alt="Architecture"
          className="absolute inset-0 w-full h-full object-cover"
        />
        <div className="absolute inset-0 bg-white/10" />
      </div>
    </div>
  );
}